<template>
  <b-card
    v-if="data"
    class="radial-bar-card"
  >
    <div class="overview-card-header">
      <div class="overview-card-heading">
        <b-card-title class="mb-0">
          {{ title }}
        </b-card-title>
        <div class="font-small-3 mb-1 text-muted">
          {{ subtitle }}
        </div>
      </div>
      <b-badge
        v-if="recent > 0"
        variant="light-success"
        pill
        class="badge-new-products"
      >
        {{ recent }} products added
        <br>
        in last week
      </b-badge>
    </div>
    <!-- apex chart -->
    <loading-container v-if="loading" />
    <div
      v-else
      class="chart-container"
    >
      <vue-apex-charts
        ref="chart"
        :key="`chart-${data.series.join('-')}`"
        type="donut"
        height="180"
        class="mt-1 donut-chart"
        :options="chartOptions"
        :series="data.series"
      />
      <div
        class="datalabel-overlay"
        @click="handleDatalabelClick"
      />
    </div>
  </b-card>
</template>

<script>
import {
  BCard, BCardTitle, BBadge,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import LoadingContainer from '@/views/components/LoadingContainer.vue'

const $textHeadingColor = '#5e5873'
export default {
  components: {
    BCard,
    BCardTitle,
    BBadge,
    VueApexCharts,
    LoadingContainer,
  },
  props: {
    data: {
      type: Object,
      default: () => {},
    },
    title: {
      type: String,
      default: 'Current Products',
    },
    subtitle: {
      type: String,
      default: 'Products currently on sale',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    total: {
      type: Number,
      default: 0,
    },
    recent: {
      type: Number,
      default: 0,
    },
  },
  beforeDestroy() {
    // Clean up ApexCharts instance to prevent conflicts with other charts
    if (window.ApexCharts) {
      const chart = window.ApexCharts.getChartByID('current-products-overview-chart')
      if (chart) {
        chart.destroy()
      }
    }
  },
  data() {
    return {
      chartOptions: {
        chart: {
          id: 'current-products-overview-chart',
          height: 190,
          type: 'donut',
          events: {
            dataPointSelection: () => {
              this.handleChartClick()
            },
          },
        },
        colors: ['#28c76f'],
        labels: ['Current'],
        states: {
          hover: {
            filter: {
              type: 'lighten',
              value: 0.1,
            },
          },
          active: {
            filter: {
              type: 'none',
            },
          },
        },
        plotOptions: {
          pie: {
            startAngle: -100,
            endAngle: 100,
            offsetY: 0,
            expandOnClick: false,
            donut: {
              size: '79%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '0.857rem',
                  fontFamily: 'Montserrat, Helvetica, Arial, serif',
                  color: $textHeadingColor,
                  offsetY: 10,
                },
                value: {
                  show: true,
                  fontSize: '1.714rem',
                  fontFamily: 'Montserrat, Helvetica, Arial, serif',
                  fontWeight: '700',
                  color: $textHeadingColor,
                  offsetY: -25,
                  formatter(val) {
                    // eslint-disable-next-line radix
                    return parseInt(val)
                  },
                },
                total: {
                  show: true,
                  label: 'Current',
                  fontSize: '0.857rem',
                  fontFamily: 'Montserrat, Helvetica, Arial, serif',
                  color: $textHeadingColor,
                  formatter: () => this.total,
                },
              },
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        legend: {
          show: false,
        },
        stroke: {
          width: 0,
        },
      },
    }
  },
  methods: {
    handleChartClick() {
      this.$router.push({
        name: 'current-products',
      })
    },
    handleDatalabelClick() {
      this.$router.push({
        name: 'current-products',
      })
    },
  },
}
</script>

<style scoped>
.radial-bar-card ::v-deep .card-body {
  position: relative;
  display: flex;
  flex-direction: column;
  padding-bottom: 0 !important;
  overflow: hidden;
}

.badge-new-products {
  font-size: 0.75rem;
  padding: 0.5rem 0.75rem;
  max-width: 45%;
  text-align: center;
  white-space: normal;
  word-break: break-word;
  line-height: 1.2;
  flex-shrink: 0;
}

.overview-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.overview-card-heading {
  min-width: 0;
  flex: 1;
}

.radial-bar-card ::v-deep .vue-apexcharts {
  display: flex;
  justify-content: center;
  align-items: flex-end;
}

.donut-chart {
  margin-bottom:-50px !important;
  margin-top: auto !important;
}

.radial-bar-card ::v-deep .apexcharts-datalabel-label,
.radial-bar-card ::v-deep .apexcharts-datalabel-value {
  fill: #5e5873 !important;
  font-size: 0.857rem !important;
  font-family: Montserrat, Helvetica, Arial, serif !important;
}

.radial-bar-card ::v-deep .apexcharts-datalabel-value {
  font-size: 1.714rem !important;
  font-weight: 700 !important;
}

body.dark-layout .radial-bar-card ::v-deep .apexcharts-datalabel-label,
body.dark-layout .radial-bar-card ::v-deep .apexcharts-datalabel-value {
  fill: #d0d2d6 !important;
}

/* Force specific styling for this chart's labels */
#current-products-overview-chart .apexcharts-datalabel-label,
#current-products-overview-chart .apexcharts-datalabel-value {
  fill: #5e5873 !important;
  font-family: Montserrat, Helvetica, Arial, serif !important;
}

#current-products-overview-chart .apexcharts-datalabel-label {
  font-size: 0.857rem !important;
}

#current-products-overview-chart .apexcharts-datalabel-value {
  font-size: 1.714rem !important;
  font-weight: 700 !important;
}

body.dark-layout #current-products-overview-chart .apexcharts-datalabel-label,
body.dark-layout #current-products-overview-chart .apexcharts-datalabel-value {
  fill: #d0d2d6 !important;
}

/* Make chart slices clickable */
#current-products-overview-chart .apexcharts-pie-series path {
  cursor: pointer;
}

.radial-bar-card ::v-deep .apexcharts-pie-series path {
  cursor: pointer;
}

.chart-container {
  position: relative;
  margin-top: auto;
}

.datalabel-overlay {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: 90px;
  height: 50px;
  cursor: pointer;
  z-index: 10;
  border-radius: 4px;
}

.datalabel-overlay:hover + .donut-chart ::v-deep .apexcharts-datalabel-label,
.datalabel-overlay:hover + .donut-chart ::v-deep .apexcharts-datalabel-value,
.chart-container:has(.datalabel-overlay:hover) ::v-deep .apexcharts-datalabel-label,
.chart-container:has(.datalabel-overlay:hover) ::v-deep .apexcharts-datalabel-value {
  fill: #1a73e8 !important;
}
</style>
